import { useEffect, useRef } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { badgeArt, VERIFIED_SEAL, VERIFIED_SPARK, CHECK_PATH } from '../../lib/badges';

// The "you earned a badge" card. Timing, sizes and motion come from the
// prototype signed off on 2026-09-24; the keyframes live in index.css (bc-*).
//
// Verified DSP gets the full treatment: the seal pops, the check DRAWS ITSELF
// (a stroke over an emptied seal — see CHECK_PATH in lib/badges), then a burst
// and the corner sparkle. Other badges are single images and get a pop.
// prefers-reduced-motion gets the finished card and a fade, handled in CSS.

// ⚠️ Only claim what verification actually unlocks server-side: create_post,
// create_reply, create_video_comment and the post-images policy all require
// verified = true.
const VERIFIED_BODY =
  'You can now post and reply in Tables, comment on videos, and share photos. Thanks for the work you do.';

const BURST_COLORS = ['#4D775E', '#7BAF8E', '#E07A5F', '#7BAF8E', '#4D775E', '#F0A98F'];

function copyFor(badges) {
  const n = badges.length;
  const hasVerified = badges.some((b) => b.id === 'verified_dsp');
  const kicker = n === 1 ? 'Badge earned' : `${n} badges earned`;

  let title;
  if (n === 1) title = hasVerified ? 'You’re a verified DSP' : badges[0].label;
  else if (n === 2) title = `${badges[0].label} and ${badges[1].label}`;
  else title = `You earned ${n} badges`;

  let body;
  if (hasVerified) body = VERIFIED_BODY;
  else if (n <= 2) body = badges.map((b) => b.description).filter(Boolean).join(' ');
  else body = badges.map((b) => b.label).join(', ');

  return { kicker, title, body };
}

function VerifiedSeal() {
  return (
    <svg viewBox="0 0 1254 1254" aria-hidden="true">
      <g className="bc-seal">
        <image href={VERIFIED_SEAL} x="0" y="0" width="1254" height="1254" />
        <path className="bc-check" d={CHECK_PATH} pathLength="1" fill="none"
          stroke="#17291D" strokeWidth="141" strokeLinecap="round" strokeLinejoin="round" />
        <path className="bc-check" d={CHECK_PATH} pathLength="1" fill="none"
          stroke="#F8F6EF" strokeWidth="86" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <image className="bc-spark" href={VERIFIED_SPARK} x="948" y="173" width="222" height="233" />
    </svg>
  );
}

function BadgeImage({ badge, className, style }) {
  const src = badgeArt(badge.id);
  if (!src) {
    return <span className={`${className} bc-emoji`} style={style} aria-hidden="true">{badge.icon}</span>;
  }
  return <img className={className} style={style} src={src} alt="" />;
}

export default function BadgeCelebration({ badges, onClose, onSeeAll }) {
  const cardRef = useRef(null);
  const artRef = useRef(null);
  useModalDismiss(onClose);

  const single = badges.length === 1;
  const isVerified = single && badges[0].id === 'verified_dsp';
  const shown = badges.slice(0, 3);
  const { kicker, title, body } = copyFor(badges);

  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, []);

  // The burst is generated, not authored: 16 bits on WAAPI, timed to land as
  // the check finishes (or as the icon settles).
  useEffect(() => {
    const art = artRef.current;
    if (!art) return undefined;
    let reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { /* old WebView */ }
    if (reduce || typeof art.animate !== 'function') return undefined;

    const delay = isVerified ? 900 : single ? 420 : 560;
    const around = isVerified ? 72 : single ? 64 : 92;
    const bits = [];
    const n = 16;
    for (let i = 0; i < n; i++) {
      const el = document.createElement('span');
      const star = i % 4 === 0;
      const size = (star ? 10 : 5) + Math.round(Math.random() * 4);
      el.className = 'bc-bit';
      Object.assign(el.style, {
        width: `${size}px`,
        height: `${size}px`,
        margin: `${-size / 2}px 0 0 ${-size / 2}px`,
        background: BURST_COLORS[i % BURST_COLORS.length],
        borderRadius: star ? '2px' : '50%',
        clipPath: star ? 'polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%)' : '',
      });
      art.appendChild(el);
      bits.push(el);
      const ang = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
      const dist = around + Math.random() * 38;
      const dx = Math.cos(ang) * dist;
      const dy = Math.sin(ang) * dist;
      el.animate([
        { opacity: 0, transform: `translate(${dx * 0.35}px, ${dy * 0.35}px) scale(.6)` },
        { opacity: 1, offset: 0.18 },
        { opacity: 0, transform: `translate(${dx}px, ${dy + 10}px) scale(.35) rotate(${star ? 120 : 0}deg)` },
      ], { duration: 820 + Math.random() * 200, delay, easing: 'cubic-bezier(.15,.7,.3,1)', fill: 'both' });
    }
    return () => bits.forEach((el) => el.remove());
  }, [isVerified, single]);

  let art;
  if (isVerified) {
    art = (
      <div ref={artRef} className="bc-art bc-art-single">
        <span className="bc-ring" />
        <VerifiedSeal />
      </div>
    );
  } else if (single) {
    art = (
      <div ref={artRef} className="bc-art bc-art-single">
        <span className="bc-ring" />
        <BadgeImage badge={badges[0]} className="bc-icon" />
      </div>
    );
  } else {
    art = (
      <div ref={artRef} className={`bc-art bc-art-row${shown.length > 2 ? ' bc-art-three' : ''}`}>
        {shown.map((b, i) => (
          <BadgeImage key={b.id} badge={b} className="bc-icon" style={{ '--bc-d': `${0.15 + i * 0.22}s` }} />
        ))}
      </div>
    );
  }

  return (
    <div className="bc-scrim fixed inset-0 z-50 flex items-center justify-center p-5" onClick={onClose}>
      <div
        ref={cardRef}
        className="bc-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bc-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {art}
        <p className="bc-kicker bc-up" style={{ '--bc-d': '.95s' }}>{kicker}</p>
        <h2 id="bc-title" className="bc-title bc-up" style={{ '--bc-d': '1s' }}>{title}</h2>
        {body && <p className="bc-body bc-up" style={{ '--bc-d': '1.06s' }}>{body}</p>}
        <button
          type="button"
          onClick={onClose}
          className="bc-up w-full py-3 rounded-xl text-[15px] font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
          style={{ '--bc-d': '1.14s', backgroundColor: 'var(--brand-primary)', color: 'white' }}
        >
          Continue
        </button>
        <button
          type="button"
          onClick={onSeeAll}
          className="bc-up mt-2 px-2 py-1 text-sm font-medium"
          style={{ '--bc-d': '1.18s', color: 'var(--brand-primary)' }}
        >
          See all badges
        </button>
      </div>
    </div>
  );
}
